// Modules
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ActivatedRoute } from '@angular/router';
import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Validators
import { passwordComplexityValidator } from '@shared/validators/password-complexity.validator';
import { passwordMatchValidator } from '@shared/validators/password-match.validator';

// Rxjs
import { Observable } from 'rxjs';

// Store - Reducer
import { AppState } from '@store/reducers';
// Store - Actions
import * as AuthActions from '@store/actions/auth.actions';
// Store - Selectors
import * as AuthSelectors from '@store/selectors/auth.selectors';

@Component({
    selector: 'sbi-cot-back-activate-account',
    templateUrl: './activate-account.component.html',
    styleUrls: ['./activate-account.component.scss'],
    standalone: false
})
export class ActivateAccountComponent implements OnInit, OnDestroy {
  // Inject DestroyRef for subscription cleanup
  private _destroyRef = inject(DestroyRef);

  // Public variables
  public activationForm: FormGroup;

  // Custom error messages for custom-text-field [errors] input
  public passwordErrors = [
    { type: 'hasUppercase', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'hasLowercase', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'hasNumber', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'hasSpecialChar', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'minLength', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'maxLength', message: 'La contraseña excede el largo máximo permitido' },
    // Populated dynamically from the server's validation message (see _handleServerError)
    { type: 'serverError', message: '' },
  ];

  public repeatPasswordErrors = [
    { type: 'passwordMismatch', message: 'Las contraseñas no coinciden' },
  ];

  // Public selectors
  public activatingUser$: Observable<boolean> = this._store.pipe(
    select(AuthSelectors.selectUserActivatingStatus)
  );
  public invalidActivationKey$: Observable<boolean> = this._store.pipe(
    select(AuthSelectors.selectUserActivationStatus)
  );

  // Constructor
  constructor(
    private _route: ActivatedRoute,
    private _store: Store<AppState>,
    private _formBuilder: FormBuilder,
    private _actions$: Actions
  ) {}

  // On component init
  ngOnInit() {
    this._buildActivationForm();
    this._validateActivationKey();
    this._handlePasswordChanges();
    this._handleServerError();
  }

  // On component destroy
  ngOnDestroy() {
    this._store.dispatch(
      AuthActions.verifyActivationResponse({ payload: { invalid: true } })
    );
  }

  // Private method to build the activation form
  private _buildActivationForm(): void {
    this.activationForm = this._formBuilder.group({
      password: ['', [Validators.required, passwordComplexityValidator()]],
      passwordRepeat: ['', [Validators.required, passwordMatchValidator('password')]],
    });
  }

  // Re-trigger match validation when password changes
  private _handlePasswordChanges(): void {
    this.activationForm
      .get('password')
      .valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.activationForm.get('passwordRepeat').updateValueAndValidity();
      });
  }

  // Private method to validate the password recovery key
  private _validateActivationKey(): void {
    const key = { key: this._route.snapshot.paramMap.get('key') };
    this._store.dispatch(
      AuthActions.verifyActivationRequested({ payload: key })
    );
  }

  // Listen for the backend validation message and show it under the field
  private _handleServerError(): void {
    this._actions$
      .pipe(
        ofType(AuthActions.activationFailed),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(action => {
        const fieldError = action.payload?.fieldError;
        const control = this.activationForm.get('password');
        this.passwordErrors.find(e => e.type === 'serverError').message =
          fieldError || '';

        if (fieldError) {
          control.setErrors({ serverError: true });
        } else if (control.hasError('serverError')) {
          control.setErrors(null);
        }
      });
  }

  // Public method to submit activation
  public onUserSubmit(): void {
    const key = this._route.snapshot.paramMap.get('key');
    const password = this.activationForm.value.password;

    this._store.dispatch(
      AuthActions.activationRequested({ payload: { key, password } })
    );
  }
}
